import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { DescriptionField } from './DescriptionField';
import { formatValuationDisplay } from '../../utils/catalogFormatters';

interface Valuation {
  value: string;
  currency: string;
  type: string;
}

interface InternalOnlyDataSectionProps {
  additionalInfoData: Record<string, unknown> | null;
}

/** Return true if a Valuation has at least one non-blank field. */
const isNonEmptyValuation = (v: Valuation): boolean =>
  v.value.trim() !== '' || v.currency.trim() !== '' || v.type.trim() !== '';

export const InternalOnlyDataSection: React.FC<InternalOnlyDataSectionProps> = ({
  additionalInfoData,
}) => {
  const privateNotes = Array.isArray(additionalInfoData?.privateNotes)
    ? (additionalInfoData?.privateNotes as string[]).filter(Boolean)
    : [];
  const physicalLocation = (additionalInfoData?.physicalLocation as string | undefined) ?? '';
  const estimatedValue = (additionalInfoData?.estimatedValue as string | undefined) ?? '';
  const markedAsSold = Boolean(additionalInfoData?.markedAsSold);
  const conditionNotes = (additionalInfoData?.conditionNotes as string | undefined) ?? '';

  // Valuations: prefer the structured array; fall back to legacy estimatedValue string.
  const rawValuations = Array.isArray(additionalInfoData?.valuations)
    ? (additionalInfoData?.valuations as unknown[]).filter(
        (v): v is Valuation =>
          typeof v === 'object' &&
          v !== null &&
          typeof (v as Valuation).value === 'string' &&
          typeof (v as Valuation).currency === 'string' &&
          typeof (v as Valuation).type === 'string',
      )
    : [];
  const nonEmptyValuations = rawValuations.filter(isNonEmptyValuation);
  const hasValuations = nonEmptyValuations.length > 0;
  const hasLegacyEstimatedValue = !hasValuations && estimatedValue.trim() !== '';

  const hasPrivateNotes = privateNotes.length > 0;
  const hasPhysicalLocation = physicalLocation.trim() !== '';
  const hasConditionNotes = conditionNotes.trim() !== '';

  if (
    !hasPrivateNotes &&
    !hasPhysicalLocation &&
    !hasValuations &&
    !hasLegacyEstimatedValue &&
    !hasConditionNotes &&
    !markedAsSold
  ) {
    return null;
  }

  // Render order mirrors the wizard's data-entry order (AdditionalInfoTab.tsx, splitPrivateSections):
  // Status (Sold) → Current Location → Private Notes → Valuation → Condition/Conservation Notes
  return (
    <Accordion data-testid="internal-only-data-section">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h3">Always Private</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          {markedAsSold && (
            <DescriptionField label="Status" value="Sold" />
          )}
          {hasPhysicalLocation && (
            <DescriptionField label="Current Location" value={physicalLocation} />
          )}
          {hasPrivateNotes && (
            <DescriptionField label="Private Notes">
              {privateNotes.map((note, idx) => (
                <Typography key={idx} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {note}
                </Typography>
              ))}
            </DescriptionField>
          )}
          {hasValuations && (
            <DescriptionField label="Valuation">
              {nonEmptyValuations.map((v, idx) => {
                const parts: string[] = [];
                if (v.type.trim()) parts.push(v.type.trim());
                const valueParts: string[] = [];
                if (v.value.trim()) valueParts.push(formatValuationDisplay(v.value.trim()));
                if (v.currency.trim()) valueParts.push(v.currency.trim());
                const valueStr = valueParts.join(' ');
                if (valueStr) parts.push(valueStr);
                return (
                  <Typography key={idx} variant="body1">
                    {parts.join(': ')}
                  </Typography>
                );
              })}
            </DescriptionField>
          )}
          {hasLegacyEstimatedValue && (
            <DescriptionField label="Valuation" value={estimatedValue} />
          )}
          {hasConditionNotes && (
            <DescriptionField label="Condition/Conservation Notes">
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {conditionNotes}
              </Typography>
            </DescriptionField>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
